/**
 * Os anexos de uma mensagem do Lucas, renderizados pelos MESMOS componentes do
 * Inbox.
 *
 * O que existia aqui antes era feito à mão: `<img>` para imagem e uma pill com
 * o nome do arquivo para todo o resto. O resultado é o que apareceu na tela —
 * documento que não baixa, imagem que não abre, áudio que não toca. Não era
 * falta de um ajuste; era uma segunda implementação, pior, do que o Inbox já
 * faz há muito tempo.
 *
 * `ImageContent` traz lightbox com zoom e download e ainda tenta de novo quando
 * o S3 ainda não publicou o arquivo. `AudioContent` traz player com waveform,
 * velocidade e transcrição. `DocumentContent` traz ícone por tipo, tamanho e
 * download. Reescrever qualquer um desses seria refazer trabalho já feito — e
 * refazer pior.
 *
 * Importados direto do arquivo, e não do barril `messages/index.js`: aquele
 * reexporta a pilha inteira de balões do Inbox, que esta tela não usa.
 */

import { ImageContent } from "@/pages/internal/inbox/components/messages/bubbles/content/ImageContent.jsx";
import { AudioContent } from "@/pages/internal/inbox/components/messages/bubbles/content/AudioContent.jsx";
import { DocumentContent } from "@/pages/internal/inbox/components/messages/bubbles/content/DocumentContent.jsx";
import { VideoContent } from "@/pages/internal/inbox/components/messages/bubbles/content/VideoContent.jsx";

/**
 * O tipo vem do mime; `isImage` fica como reserva porque o anexo otimista,
 * montado antes do upload terminar, é quem carrega essa marca.
 */
function kindOf(attachment) {
  const mime = attachment?.mimeType || "";
  if (mime.startsWith("image/") || attachment?.isImage) return "image";
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("video/")) return "video";
  return "document";
}

export default function MessageAttachments({ attachments, className = "" }) {
  const files = (attachments || []).filter((a) => a?.url || a?.previewUrl);
  if (!files.length) return null;

  return (
    /**
     * Sem `w-full`: largura 100% anula o alinhamento do pai.
     *
     * O pai é `flex-col items-end` na fala dela e `items-start` na do Lucas.
     * Um filho com `width: 100%` ocupa a linha inteira, então `items-end` não
     * tem para onde empurrar — a caixa de 420px ancorava à ESQUERDA e o cartão
     * do arquivo aparecia desencostado da bolha da mensagem.
     *
     * Sem largura declarada, o item encolhe até o conteúdo e o alinhamento do
     * pai volta a valer nos dois lados.
     */
    <div className={`flex max-w-[420px] flex-col gap-2 ${className}`}>
      {files.map((attachment) => {
        const key = attachment.id || attachment.url || attachment.name;
        const src = attachment.url || attachment.previewUrl;

        switch (kindOf(attachment)) {
          case "image":
            return <ImageContent key={key} src={src} caption={attachment.caption} />;
          case "video":
            return <VideoContent key={key} src={src} caption={attachment.caption} />;
          case "audio":
            return <AudioContent key={key} src={src} duration={attachment.duration} />;
          default:
            return (
              <DocumentContent
                key={key}
                filename={attachment.name || "arquivo"}
                size={attachment.size}
                mimeType={attachment.mimeType}
                downloadUrl={src}
                caption={attachment.caption}
              />
            );
        }
      })}
    </div>
  );
}
